<template>
    <div class="main-container purger-app-page">
        <router-view></router-view>

        <section class="purger-hero section-space--pt_90 section-space--pb_90">
            <div class="container">
                <div class="row align-items-center">
                    <div class="col-lg-3 col-md-4 text-center text-md-left mb-30 mb-md-0">
                        <img :src="appIcon" class="purger-app-icon" alt="Purger for Plex icon">
                    </div>
                    <div class="col-lg-9 col-md-8">
                        <div class="purger-hero-content">
                            <h1 class="purger-title">Purger for Plex</h1>
                            <p class="purger-subtitle">Swipe. Clean. Reclaim.</p>
                            <a class="purger-store-button" href="https://apps.apple.com/app/id6757200064" target="_blank" rel="noopener" aria-label="iOS download">
                                <img :src="iosBadge" alt="iOS">
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="purger-screenshots section-space--pt_60 section-space--pb_90">
            <div class="container">
                    <div class="section-title text-left section-space--mb_40">
                    <h3 class="mb-10">Screenshots</h3>
                </div>
                <div class="swiper-container purger-screenshots__container">
                    <swiper :options="swiperOption">
                        <div class="swiper-slide" v-for="(shot, index) in screenshots" :key="index">
                            <div class="purger-shot">
                                <img :src="shot" alt="Purger for Plex screenshot">
                            </div>
                        </div>
                        <div class="swiper-pagination purger-screenshots-pagination" slot="pagination"></div>
                        <div class="swiper-button-prev purger-screenshots-prev" slot="button-prev"></div>
                        <div class="swiper-button-next purger-screenshots-next" slot="button-next"></div>
                    </swiper>
                </div>
            </div>
        </section>

        <section class="purger-description section-space--pb_120">
            <div class="container">
                <div class="row">
                    <div class="col-lg-10">
                        <div class="section-title text-left">
                            <h3 class="mb-20">Description</h3>
                            <p>Is your Plex Server screaming for mercy? Stop digging through web UI menus to find space. Purger for Plex transforms library maintenance from a chore into a high-speed, deck-based curation experience. Reclaim your Terabytes with a swipe.</p>
                            <h4 class="mt-30 purger-section-title">THE SWIPE WORKFLOW</h4>
                            <p>Stop the endless scrolling. Purger presents your media as a stack of actionable cards.</p>
                            <ul>
                                <li>Swipe Left: Stage for deletion.</li>
                                <li>Swipe Right: Keep in library.</li>
                                <li>Haptic Feedback and Visual Overlays: Real-time confirmation of every action.</li>
                                <li>High-Velocity Curation: Process hundreds of movies or entire TV seasons in a single session.</li>
                            </ul>
                            <h4 class="mt-30 purger-section-title">DATA-DRIVEN DECISIONS</h4>
                            <p>Don't guess what to delete. Make informed decisions using aggregated metadata and server-side analytics:</p>
                            <ul>
                                <li>Global Watch Counts: See if anyone on your server has actually watched that 80GB 4K Remux.</li>
                                <li>Aggregated Ratings: Instant access to Rotten Tomatoes (Critics/Audience), IMDb, and TMDb scores.</li>
                                <li>Technical Specs: View file info at a glance to identify storage hogs.</li>
                            </ul>
                            <h4 class="mt-30 purger-section-title">PRO-LEVEL MANAGEMENT</h4>
                            <p>Built for the power user who demands precision:</p>
                            <ul>
                                <li>Advanced Filtering: Sort by file size, release year, bitrate, or "never watched" status.</li>
                                <li>The Staging Area: Nothing is deleted until you review the final queue. Guard against accidental "Left Swipes."</li>
                                <li>Session Persistence: Close the app mid-cleanup? Purger remembers exactly where you left off in your library.</li>
                                <li>Batch Atomicity: A real-time progress bar tracks deletions across your local or remote filesystem.</li>
                            </ul>
                            <h4 class="mt-30 purger-section-title">SEAMLESS CONNECTIVITY</h4>
                            <p>Whether you are on your home LAN or commuting:</p>
                            <ul>
                                <li>Remote Access: Full support for Plex Relay and manual Port Forwarding connections.</li>
                                <li>Adaptive Networking: Automatically switches between local IP and remote URL for the lowest latency.</li>
                                <li>Dark Mode Optimized: A sleek, OLED-friendly interface designed for late-night server maintenance.</li>
                            </ul>
                            <h4 class="mt-30 purger-section-title">SAFE AND SECURE</h4>
                            <ul>
                                <li>Read/Write Precision: Designed specifically for the Plex Media Server API.</li>
                                <li>Review Mode: Double-check your "burn list" before committing changes to the disk.</li>
                                <li>Privacy First: Your server credentials never leave your device.</li>
                            </ul>
                            <p class="mt-30">Stop the storage bloat. Start swiping.</p>
                            <p>Note: Requires an active Plex Media Server with "Allow Media Deletion" enabled in settings. Proceed with caution: Deletions are permanent.</p>
                            <p>Purger for Plex is not affiliated with Plex Inc.</p>
                        </div>
                    </div>
                </div>
                <div class="purger-legal-links">
                    <router-link to="/apps/purger/terms">T&amp;C</router-link>
                    <router-link to="/apps/purger/privacy">Privacy</router-link>
                </div>
            </div>
        </section>

        <FooterMain class="border-top" />
        <OffcanvasSearchBox />
        <OffCanvasMobileMenu />
    </div>
</template>

<script>
    import FooterMain from '@/components/FooterMain'
    import OffcanvasSearchBox from '@/components/OffcanvasSearchBox'
    import OffCanvasMobileMenu from '@/components/OffCanvasMobileMenu'

    export default {
        name: 'PurgerForPlex',
        components: {
            FooterMain,
            OffcanvasSearchBox,
            OffCanvasMobileMenu
        },
        data() {
            return {
                appIcon: require('@/assets/img/apps/purger/icon.svg'),
                iosBadge: require('@/assets/img/apps/ios.svg'),
                screenshots: [
                    require('@/assets/img/apps/purger/screenshots/iphone69_screenshot-1.jpeg'),
                    require('@/assets/img/apps/purger/screenshots/iphone69_screenshot-2.jpeg'),
                    require('@/assets/img/apps/purger/screenshots/iphone69_screenshot-3.jpeg'),
                    require('@/assets/img/apps/purger/screenshots/iphone69_screenshot-4.jpeg')
                ],
                swiperOption: {
                    slidesPerView: 4,
                    slidesPerGroup: 1,
                    spaceBetween: 24,
                    loop: false,
                    pagination: {
                        el: '.purger-screenshots-pagination',
                        clickable: true
                    },
                    navigation: {
                        nextEl: '.purger-screenshots-next',
                        prevEl: '.purger-screenshots-prev'
                    },
                    breakpoints: {
                        0: {
                            slidesPerView: 1,
                            spaceBetween: 16
                        },
                        768: {
                            slidesPerView: 2,
                            spaceBetween: 20
                        },
                        992: {
                            slidesPerView: 3,
                            spaceBetween: 22
                        },
                        1200: {
                            slidesPerView: 4,
                            spaceBetween: 24
                        }
                    }
                }
            }
        },
        metaInfo: {
            title: 'Dualbit - Purger for Plex',
            htmlAttrs: {
                lang: 'en',
                amp: true
            }
        }
    }
</script>

<style lang="scss" scoped>
    .purger-hero {
        background: linear-gradient(135deg, rgba(39, 57, 124, 0.08), rgba(39, 57, 124, 0));
        padding-top: 180px;
        @media (max-width: 1199px) {
            padding-top: 140px;
        }
        @media (max-width: 767px) {
            padding-top: 70px;
        }
    }

    .purger-app-icon {
        width: 140px;
        height: 140px;
        border-radius: 28px;
        box-shadow: 0 18px 40px rgba(0, 0, 0, 0.12);
        object-fit: cover;
    }

    .purger-title {
        font-size: 38px;
        font-weight: 700;
        margin-bottom: 10px;
    }

    .purger-subtitle {
        font-size: 18px;
        margin-bottom: 24px;
    }

    .purger-description .section-title h3 {
        font-size: 26px;
    }

    .purger-section-title {
        font-size: 16px;
        letter-spacing: 1px;
    }

    .purger-legal-links {
        margin-top: 24px;
        display: flex;
        gap: 24px;
    }

    .purger-legal-links a {
        font-weight: 600;
        color: #27397c;
    }

    .purger-store-button img {
        height: 48px;
        display: inline-block;
    }

    .purger-screenshots__container {
        position: relative;
    }

    .purger-screenshots {
        margin-top: 8px;
        @media (max-width: 767px) {
            margin-top: 6px;
        }
    }

    .purger-shot {
        border-radius: 18px;
        overflow: hidden;
        box-shadow: 0 14px 30px rgba(0, 0, 0, 0.1);
        background-color: #ffffff;
    }

    .purger-shot img {
        width: 100%;
        display: block;
    }

    .purger-screenshots-prev,
    .purger-screenshots-next {
        color: #27397c;
    }
</style>
